import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase/config';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const ITEMS_PER_PAGE = 20;

const TYPE_CONFIG = {
  PROCESS_PDF: { label: 'Conversion', badgeClass: 'bg-amber-100 text-amber-800', sign: '-' },
  ADD: { label: 'Purchase', badgeClass: 'bg-green-100 text-green-800', sign: '+' },
  REFUND_AUTO: { label: 'Refund', badgeClass: 'bg-blue-100 text-blue-800', sign: '+' },
  REFUND_ADJUSTMENT: { label: 'Refund', badgeClass: 'bg-blue-100 text-blue-800', sign: '+' },
  DEDUCT: { label: 'Deduction', badgeClass: 'bg-red-100 text-red-800', sign: '-' },
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All Activity' },
  { value: 'conversions', label: 'Conversions only' },
  { value: 'purchases', label: 'Purchases only' },
  { value: 'refunds', label: 'Refunds only' },
];

const formatDate = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const datePart = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  return `${datePart}, ${hours}:${minutes}`;
};

const formatDateForCSV = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString('en-GB', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
};

const matchesFilter = (log, filter) => {
  if (filter === 'all') return true;
  if (filter === 'conversions') return log.type === 'PROCESS_PDF';
  if (filter === 'purchases') return log.type === 'ADD';
  if (filter === 'refunds') return log.type === 'REFUND_AUTO' || log.type === 'REFUND_ADJUSTMENT';
  return true;
};

const matchesDateRange = (log, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return true;
  const logDate = log.timestamp;
  if (dateFrom) {
    const from = new Date(dateFrom);
    from.setHours(0, 0, 0, 0);
    if (logDate < from) return false;
  }
  if (dateTo) {
    const to = new Date(dateTo);
    to.setHours(23, 59, 59, 999);
    if (logDate > to) return false;
  }
  return true;
};

const CreditActivity = () => {
  const { currentUser, userCredits } = useAuth();
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);

  useEffect(() => {
    if (!currentUser) {
      setLogs([]);
      setLoading(false);
      return;
    }

    const logsRef = collection(db, 'creditLogs');
    const q = query(
      logsRef,
      where('userId', '==', currentUser.uid),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        const logList = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            timestamp: data.timestamp?.toDate() || new Date()
          };
        });
        setLogs(logList);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Credit logs query error:', err);
        setError('Failed to load credit activity');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [filter, dateFrom, dateTo]);

  const filteredLogs = useMemo(() => {
    return logs.filter(log => matchesFilter(log, filter) && matchesDateRange(log, dateFrom, dateTo));
  }, [logs, filter, dateFrom, dateTo]);

  const totalPages = Math.ceil(filteredLogs.length / ITEMS_PER_PAGE);
  const paginatedLogs = filteredLogs.slice(page * ITEMS_PER_PAGE, (page + 1) * ITEMS_PER_PAGE);
  const showingFrom = filteredLogs.length === 0 ? 0 : page * ITEMS_PER_PAGE + 1;
  const showingTo = Math.min((page + 1) * ITEMS_PER_PAGE, filteredLogs.length);

  const handleExportCSV = () => {
    const headers = ['Date', 'Type', 'Details', 'Credits', 'Balance'];
    const rows = filteredLogs.map(log => {
      const config = TYPE_CONFIG[log.type] || { label: log.type, sign: '' };
      const creditValue = config.sign === '+' ? `+${log.amount}` : `-${log.amount}`;
      return [
        formatDateForCSV(log.timestamp),
        config.label,
        `"${(log.reason || '').replace(/"/g, '""')}"`,
        creditValue,
        log.balanceAfter ?? ''
      ];
    });

    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `credit-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Activity</h2>
            <div className="animate-pulse space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-200 rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Credit Activity</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Dashboard
          </button>
          <div className="text-sm text-gray-600">
            Credits: <span className="font-semibold text-green-600">{userCredits || 0}</span>
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-lg shadow">
          {/* Card Header & Filters */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Credit Activity</h2>
              <button
                onClick={handleExportCSV}
                disabled={filteredLogs.length === 0}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed bg-gray-50 hover:bg-gray-100 disabled:hover:bg-gray-50 border border-gray-200 px-3 py-2 rounded-lg transition-colors"
              >
                <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                Export CSV
              </button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-sm text-gray-500">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          {filteredLogs.length === 0 ? (
            <div className="text-center py-12">
              <InformationCircleIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No credit activity yet</h3>
              <p className="text-gray-500">
                Credit activity will appear here as you use the service
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Credits</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedLogs.map((log) => {
                      const config = TYPE_CONFIG[log.type] || { label: log.type, badgeClass: 'bg-gray-100 text-gray-800', sign: '' };
                      const isPositive = config.sign === '+';
                      return (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(log.timestamp)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.badgeClass}`}>
                              {config.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                            {log.reason || '—'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-right ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                            {isPositive ? '+' : '-'}{log.amount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                            {log.balanceAfter ?? '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  Showing {showingFrom}–{showingTo} of {filteredLogs.length} entries
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => p - 1)}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreditActivity;
